import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import Api from 'api';
import { SimpleModal } from 'components/Modal';
import { StyledButton } from 'components/Buttons/DefaultButton/DefaultButton.styles';
import useInvoiceInformation from 'hooks/useInvoiceInformation';
import { informationDisplayDuration, InvoiceInformationItem } from 'utils/invoiceInformation';

export default function InvoiceInformationRequest({ invoiceId, settled = false }: { invoiceId: number; settled?: boolean }) {
  const { data, pending, error, refresh, mutate } = useInvoiceInformation(invoiceId, settled);
  const [picker, setPicker] = useState(false);
  const [item, setItem] = useState<InvoiceInformationItem | ''>('');
  const [message, setMessage] = useState('');
  const [expired, setExpired] = useState(false);
  useEffect(() => { setPicker(false); setItem(''); setMessage(''); }, [invoiceId, settled]);
  useEffect(() => {
    setExpired(false);
    if (!data || data.request?.status !== 'shared') return;
    const remaining = informationDisplayDuration(data);
    if (!Number.isFinite(remaining)) { setExpired(true); return; }
    const timer = setTimeout(() => { setExpired(true); void refresh(); }, Math.min(remaining, 2147483647));
    return () => clearTimeout(timer);
  }, [data]);
  if (settled) return null;
  const request = data?.request;
  const send = async () => {
    if (!item || !data?.canRequest) return;
    const receipt = await mutate(() => Api.requestInvoiceInformation(invoiceId, item));
    if (receipt) { setPicker(false); setItem(''); if (receipt.kind === 'mailing_address') setMessage(receipt.message || "The Talent's mailing address is already printed below their name on the invoice."); }
  };
  return <section data-testid="invoice-information-request" className="list-block inset">
    {error && <p role="alert">{error}</p>}
    {!data && <StyledButton variant="text" onClick={refresh} disabled={pending} data-testid="invoice-information-refresh">{pending ? 'Checking payment information…' : 'Refresh status'}</StyledButton>}
    {data?.canRequest && <><p>Not using Pay Now?</p><StyledButton variant="text" data-testid="invoice-information-open" disabled={pending}
      onClick={() => { setPicker(true); setItem(''); }}>What information do you need?</StyledButton></>}
    {request && <div data-testid="invoice-information-status"><h3>Payment info request</h3>
      {request.status === 'awaiting_response' && <p>{data?.paymentEmailLabel} — requested {dayjs(request.requestedOn).format('MMM D, YYYY')}, awaiting response.</p>}
      {request.status === 'contact_requested' && <p>Direct deposit — {data?.talentFirstName} has been asked to contact you directly.</p>}
      {request.status === 'shared' && !expired && <p data-testid="invoice-information-shared">{data?.paymentEmailLabel}: {request.paymentEmail} — shared {dayjs(request.sharedOn).format('MMM D, YYYY, h:mm A')}. Available for 3 days after sharing.</p>}
      {(request.status === 'expired' || expired) && <p>Shared {dayjs(request.sharedOn).format('MMM D, YYYY')} — no longer displayed.</p>}
      {request.canAskAgain && request.status === 'expired' && <StyledButton variant="outlined" disabled={pending} data-testid="invoice-information-renew"
        onClick={() => void mutate(() => Api.renewInvoiceInformation(invoiceId, request.generation))}>Ask again</StyledButton>}
    </div>}
    <SimpleModal isOpen={picker && !!data?.canRequest}>
      <h2>What information do you need?</h2>
      <fieldset disabled={pending}><legend>Select one item</legend>
        {([{ value: 'payment_email', label: data?.paymentEmailLabel }, { value: 'banking_contact', label: 'Banking info for direct deposit' },
          { value: 'mailing_address', label: 'Mailing address (for cheque)' }] as const).map(option => <p key={option.value}><label>
            <input type="radio" name="invoice-information-item" value={option.value} checked={item === option.value}
              data-testid={`invoice-information-${option.value}`} onChange={() => setItem(option.value)} /> {option.label}
          </label></p>)}
      </fieldset>
      <StyledButton variant="contained" disabled={!item || pending} onClick={send} data-testid="invoice-information-send">Send</StyledButton>
      <StyledButton variant="text" disabled={pending} onClick={() => setPicker(false)}>Cancel</StyledButton>
    </SimpleModal>
    <SimpleModal isOpen={!!message}><p>{message}</p><StyledButton onClick={() => setMessage('')}>OK</StyledButton></SimpleModal>
  </section>;
}
